import { useQuery } from '@tanstack/react-query';
import { request } from 'utils/request';
import { header } from 'utils/auth';
import { formatToDealerTime } from 'utils/dateUtils';

const LAST_INVENTORY_SYNC_DATE_ENDPOINT =
  '/api/v2/dealer-inventory/last-inventory-sync-date';

export interface LastInventorySyncDateApiResponse {
  body?: { last_inventory_sync_date?: string | null };
  message?: string;
  statusCode?: number;
}

export interface LastInventorySyncDateResponse {
  last_inventory_sync_date: string | null;
}

async function fetchLastInventorySyncDate(
  dealerId: string,
): Promise<LastInventorySyncDateResponse> {
  const url = `${LAST_INVENTORY_SYNC_DATE_ENDPOINT}/${dealerId}`;
  const response = (await request(url, {
    method: 'GET',
    headers: new Headers({
      'Content-Type': 'application/json',
      Authorization: header(),
    }),
  })) as LastInventorySyncDateApiResponse | undefined;
  if (response && typeof response === 'object') {
    const value = response.body?.last_inventory_sync_date ?? null;
    return { last_inventory_sync_date: value };
  }
  throw new Error('Invalid response from last inventory sync date API');
}

function formatLastInventorySyncDate(isoDate: string | null): string {
  if (isoDate === null || isoDate === undefined) return 'No inventory';
  const formatted = formatToDealerTime(isoDate);
  return formatted === 'Invalid Date' ? 'Invalid date' : formatted;
}

export interface UseLastInventorySyncDateResult {
  /** Formatted display string: date or "No inventory" or "Invalid date" */
  displayText: string;
  /** Raw value from API */
  lastInventorySyncDate: string | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

export function useLastInventorySyncDate(
  dealerId: string | undefined,
): UseLastInventorySyncDateResult {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['last-inventory-sync-date', dealerId],
    queryFn: () => fetchLastInventorySyncDate(dealerId!),
    enabled: Boolean(dealerId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const lastInventorySyncDate = data?.last_inventory_sync_date ?? null;
  const displayText = isLoading
    ? 'Loading...'
    : isError
      ? 'Unable to load'
      : formatLastInventorySyncDate(lastInventorySyncDate);

  return {
    displayText,
    lastInventorySyncDate,
    isLoading,
    isError,
    error: error as Error | null,
  };
}
